import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { publicUrl } from "@/lib/storage";

type Post = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  cover_image_path: string | null;
  published_at: string | null;
};

const Spotlight = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, slug, title, subtitle, cover_image_path, published_at")
        .eq("published", true)
        .order("sort_order")
        .order("published_at", { ascending: false, nullsFirst: false });
      setPosts((data as any) ?? []);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="py-16 md:py-24">
          <div className="container">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Celebrate</span>
              <h1 className="text-5xl md:text-7xl mt-3 text-balance">Athlete Spotlight.</h1>
              <p className="text-muted-foreground mt-3">
                Stories, interviews, and features on the women redefining sport.
              </p>
            </div>

            {loading ? (
              <p className="text-muted-foreground mt-10">Loading…</p>
            ) : posts.length === 0 ? (
              <p className="text-muted-foreground mt-10">Coming soon...</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                {posts.map((p) => {
                  const img = publicUrl(p.cover_image_path);
                  return (
                    <Link
                      key={p.id}
                      to={`/spotlight/${p.slug}`}
                      className="group rounded-2xl overflow-hidden bg-card shadow-card hover:shadow-bold transition-all"
                    >
                      <div className="aspect-[4/3] bg-muted overflow-hidden">
                        {img ? (
                          <img
                            src={img}
                            alt={p.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                            No cover
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <h2 className="font-display text-2xl leading-tight">{p.title}</h2>
                        {p.subtitle && <p className="text-sm text-muted-foreground mt-2">{p.subtitle}</p>}
                        {p.published_at && (
                          <p className="text-xs text-muted-foreground mt-3">
                            {new Date(p.published_at).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Spotlight;
